// Dropdown — anchored popover menu. Self-manages open state and closes on
// outside click / Esc. The trigger is a render prop so the caller controls the
// button; children are the menu contents. Base for the header notification /
// profile / language menus.
//
// `portal`: when true, the menu is rendered into document.body (fixed-positioned
// from the trigger) instead of as an absolute child. Use this when the trigger
// lives inside a clipping (`overflow`) or stacking ancestor that would otherwise
// cut the menu off — e.g. the Portfolio holdings card. Default false keeps the
// original in-flow behavior for existing callers.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import './dropdown.css';

export function Dropdown({
  trigger,
  align = 'right',
  width,
  className,
  menuClassName,
  portal = false,
  children,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null); // trigger wrapper
  const menuRef = useRef(null); // menu surface (in-flow or portaled)

  const close = useCallback(() => setOpen(false), []);
  const toggle = () => setOpen((v) => !v);

  // Close on outside pointerdown / Esc. Checks BOTH the trigger wrapper and the
  // menu so a click on a portaled option (outside `ref`) still selects instead
  // of closing first.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Defer binding so the opening click doesn't immediately close the menu.
    const t = setTimeout(() => {
      document.addEventListener('pointerdown', onPointer);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position the portaled menu relative to the trigger, capped to the space
  // between the sticky header and fixed footer (via --header-h / --footer-h) so
  // it never overlaps the app chrome — flipping above the trigger when there's
  // more room there. The menu follows the trigger on scroll/resize and only
  // closes once the trigger scrolls out of the visible content band.
  useLayoutEffect(() => {
    if (!portal || !open || !ref.current) return undefined;
    const GAP = 6;
    // `allowClose` only on scroll/resize: once the trigger scrolls out of the
    // content band there's nothing to anchor to. On the initial open we always
    // place (flipping up when there's no room below), never close.
    const place = (allowClose) => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const cs = getComputedStyle(document.documentElement);
      const headerH = parseInt(cs.getPropertyValue('--header-h'), 10) || 0;
      const footerH = parseInt(cs.getPropertyValue('--footer-h'), 10) || 0;
      if (allowClose && (r.bottom <= headerH || r.top >= window.innerHeight - footerH)) {
        setOpen(false);
        return;
      }
      const spaceBelow = window.innerHeight - r.bottom - footerH - GAP;
      const spaceAbove = r.top - headerH - GAP;
      const horizontal =
        align === 'left' ? { left: r.left } : { right: window.innerWidth - r.right };
      const flipUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      setCoords(
        flipUp
          ? { bottom: window.innerHeight - r.top + GAP, ...horizontal, maxHeight: Math.max(spaceAbove, 120) }
          : { top: r.bottom + GAP, ...horizontal, maxHeight: Math.max(spaceBelow, 120) },
      );
    };
    place(false);
    // Follow the trigger on viewport scroll/resize; ignore scrolls inside the menu.
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      place(true);
    };
    const onResize = () => place(true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [portal, open, align]);

  const menuContent = typeof children === 'function' ? children({ close }) : children;

  const inFlowMenu = open && !portal && (
    <div
      ref={menuRef}
      className={cx('dropdown__menu', `dropdown__menu--${align}`, menuClassName)}
      style={width ? { width } : undefined}
      role="menu"
    >
      {menuContent}
    </div>
  );

  // Explicit `auto` on the unused sides overrides the base
  // `.dropdown__menu { top: calc(100% + 6px) }` rule, which would otherwise
  // resolve to 100vh on this fixed element and push a flip-up (bottom-anchored)
  // menu off-screen.
  const portaledMenu =
    open && portal && coords
      ? createPortal(
          <div
            ref={menuRef}
            className={cx('dropdown__menu', 'dropdown__menu--portal', menuClassName)}
            style={{
              position: 'fixed',
              top: coords.top != null ? coords.top : 'auto',
              bottom: coords.bottom != null ? coords.bottom : 'auto',
              left: coords.left != null ? coords.left : 'auto',
              right: coords.right != null ? coords.right : 'auto',
              width,
              maxHeight: coords.maxHeight,
              overflowY: 'auto',
            }}
            role="menu"
          >
            {menuContent}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={cx('dropdown', className)} ref={ref}>
      {trigger({ open, toggle, close })}
      {inFlowMenu}
      {portaledMenu}
    </div>
  );
}

Dropdown.propTypes = {
  /** render prop: ({ open, toggle, close }) => triggerNode */
  trigger: PropTypes.func.isRequired,
  align: PropTypes.oneOf(['left', 'right']),
  width: PropTypes.number,
  className: PropTypes.string,
  /** Extra class for the menu surface (e.g. to raise z-index over a drawer). */
  menuClassName: PropTypes.string,
  /** Render the menu in a body portal (escapes clipping/stacking ancestors). */
  portal: PropTypes.bool,
  /** menu contents, or a render prop ({ close }) => node */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default Dropdown;
